import { Link } from "react-router-dom";
import { routesMap } from "@/components/routes/routes";
import freeDomainList from "@/statics/freeEmailDomains.json";
import OpticksCheckbox from "@/components/ui/OpticksCheckbox/OpticksCheckbox";
import OpticksButton from "@/components/ui/OpticksButton/OpticksButton";
import OpticksInput from "@/components/ui/OpticksInput/OpticksInput";
import OpticksLink from "@/components/ui/OpticksLink/OpticksLink";
import type { SignupFormPayload } from "@/types/model/Auth";
import { Controller, useForm } from "react-hook-form";
import { doSignup } from "@/services/service-layer/authService";
import OpticksLogo from "@/components/ui/OpticksLogos/OpticksLogo";

const Signup = () => {
  const doSignupFunction = (payload: SignupFormPayload) => {
    doSignup({
      ...payload,
      context: {
        pageUri: window.location.href,
        pageName: "Register",
      },
    });
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<SignupFormPayload>({
    mode: "onSubmit",
    defaultValues: {
      email: "",
      firstName: "",
      companyName: "",
      jobTitle: "",
      password: "",
      terms: false,
    },
  });

  return (
    <>
      <h2>
        Sign up to <OpticksLogo />
      </h2>
      <form
        className="auth-form"
        noValidate={true}
        onSubmit={handleSubmit(doSignupFunction)}
      >
        <div className="fields">
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Required",
              validate: (value) => {
                if (!/\S+@\S+\.\S{2,}/.test(value)) {
                  return "Invalid";
                }
                if (freeDomainList.includes(value.split("@")[1] ?? "")) {
                  return "Please enter a valid business email";
                }
                return true;
              },
            }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Email"
                  type="email"
                  required
                  surface="white"
                  input={field}
                />
                {errors.email && (
                  <span className="form-invalid-message">
                    {errors.email.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="firstName"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="First name"
                  type="text"
                  required
                  surface="white"
                  input={field}
                />
                {errors.firstName && (
                  <span className="form-invalid-message">
                    {errors.firstName.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="companyName"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Company name"
                  type="text"
                  required
                  surface="white"
                  input={field}
                />
                {errors.companyName && (
                  <span className="form-invalid-message">
                    {errors.companyName.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="jobTitle"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Job Title"
                  type="text"
                  required
                  surface="white"
                  input={field}
                />
                {errors.jobTitle && (
                  <span className="form-invalid-message">
                    {errors.jobTitle.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Password"
                  type="password"
                  required
                  surface="white"
                  input={field}
                />
                {errors.password && (
                  <span className="form-invalid-message">
                    {errors.password.message}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="terms"
            control={control}
            rules={{
              validate: (value) => value === true || "Required",
            }}
            render={({ field }) => (
              <div>
                <OpticksCheckbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  <span>
                    I accept the{" "}
                    <OpticksLink
                      href="https://optickssecurity.com/resources/files/OPTICKS_privacy_policy.pdf"
                      label="terms and conditions"
                    />
                  </span>
                </OpticksCheckbox>

                {errors.terms && (
                  <span className="form-invalid-message">
                    {errors.terms.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        <div className="login-btn">
          <OpticksButton
            type="submit"
            size="m-wide"
            disabled={!isValid || isSubmitting}
            label="Sign Up"
          />
        </div>

        <div className="login-account">
          Already have an account?{" "}
          <Link to={routesMap.auth.login.path}>Login</Link>
        </div>
      </form>
    </>
  );
};

export default Signup;
